import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Shield, Lock, Eye, Server, Database, UserCheck, Bell, Trash2, FileDown, Mail } from 'lucide-react';

export const Privacy = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid="privacy-page">
        {/* Hero */}
        <section className="py-16 px-4 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="h-14 w-14 mx-auto mb-4 opacity-90" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 font-heading" data-testid="privacy-title">Privacy Policy</h1>
            <p className="text-lg text-slate-300">Your data protection is our highest priority</p>
            <p className="text-sm text-slate-400 mt-3">Last updated: February 2026 | Effective for users in India</p>
          </div>
        </section>

        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Quick Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              {[
                { icon: Lock, title: 'Encrypted', text: 'AES-256 at rest, TLS 1.3 in transit' },
                { icon: Eye, title: 'No Selling', text: 'We never sell your personal data' },
                { icon: Database, title: 'India Stored', text: 'Data stored in Indian data centres' }
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <Card key={i} className="text-center border-none shadow-md" data-testid={`privacy-card-${i}`}>
                    <CardContent className="p-6">
                      <Icon className="h-8 w-8 text-teal-600 mx-auto mb-3" />
                      <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-600">{item.text}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="prose prose-slate max-w-none space-y-10">
              {/* 1. Introduction */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">1. Introduction</h2>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  Ariskool Technologies Pvt. Ltd. ("Ariskool", "we", "us", or "our"), registered in Bangalore, Karnataka, India, 
                  is committed to protecting the privacy of schools, administrators, teachers, and staff who use our platform. 
                  This Privacy Policy explains how we collect, use, store, and protect your personal data in compliance with the 
                  <strong> Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and the 
                  <strong> Information Technology Act, 2000</strong>.
                </p>
              </section>

              {/* 2. Information We Collect */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Database className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">2. Information We Collect</h2>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mt-5 mb-2">A. Personal Information (provided by you)</h3>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>Full name, email address, phone number</li>
                  <li>School name, state, district, and school board affiliation</li>
                  <li>Employee/Teacher ID and department</li>
                  <li>Password (stored as a salted hash, never in plain text)</li>
                  <li>Leave request details — dates, leave type, reason</li>
                  <li>Supporting documents (medical certificates, etc.)</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-900 mt-5 mb-2">B. Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>IP address and approximate location (city-level)</li>
                  <li>Browser type, device type, and operating system</li>
                  <li>Pages visited, features used, and session duration</li>
                  <li>Login timestamps and access logs</li>
                </ul>

                <div className="bg-teal-50 border border-indigo-100 rounded-lg p-4 mt-4">
                  <p className="text-sm text-indigo-800 m-0">
                    <strong>Note:</strong> We do not collect Aadhaar numbers, biometric data, caste information, 
                    or any Sensitive Personal Data as defined under the IT Act unless explicitly required and consented to.
                  </p>
                </div>
              </section>

              {/* 3. How We Use Your Data */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Eye className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">3. How We Use Your Data</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { title: 'Service Delivery', text: 'Process leave requests, manage accounts, track balances' },
                    { title: 'Notifications', text: 'Send in-app alerts for approvals, rejections, and reminders' },
                    { title: 'Analytics', text: 'Generate reports and dashboards for school admins' },
                    { title: 'Security', text: 'Detect fraud, prevent unauthorized access, audit logs' },
                    { title: 'Support', text: 'Respond to queries and resolve technical issues' },
                    { title: 'Improvement', text: 'Analyse usage patterns to improve features and UX' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="h-6 w-6 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm m-0">{item.title}</p>
                        <p className="text-sm text-slate-600 m-0">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 4. Data Sharing */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <UserCheck className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">4. Data Sharing & Disclosure</h2>
                </div>
                <p className="text-slate-700 leading-relaxed font-semibold">We do NOT sell, rent, or trade your personal data.</p>
                <p className="text-slate-700 leading-relaxed">We may share data only in these cases:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li><strong>Within Your School:</strong> Admins/HR can view leave data of staff within the same tenant</li>
                  <li><strong>Service Providers:</strong> Cloud infrastructure (AWS Mumbai region), payment processing (Stripe India)</li>
                  <li><strong>Legal Obligation:</strong> When required by Indian courts, government agencies, or law enforcement</li>
                  <li><strong>School Board Compliance:</strong> If required by CBSE, ICSE, or State Education Boards for audits</li>
                </ul>
              </section>

              {/* 5. Data Storage & Security */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Server className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">5. Data Storage & Security</h2>
                </div>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li><strong>Location:</strong> All data is stored in Indian data centres (Mumbai region)</li>
                  <li><strong>Encryption:</strong> AES-256 encryption at rest, TLS 1.3 for data in transit</li>
                  <li><strong>Access Control:</strong> Role-based access with JWT authentication and tenant isolation</li>
                  <li><strong>Backups:</strong> Daily automated backups with 30-day retention</li>
                  <li><strong>Audits:</strong> Regular security audits and vulnerability assessments</li>
                  <li><strong>Passwords:</strong> Stored using bcrypt hashing — never in plain text</li>
                </ul>
              </section>

              {/* 6. Your Rights under DPDP Act */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <FileDown className="h-5 w-5 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">6. Your Rights (DPDP Act, 2023)</h2>
                </div>
                <p className="text-slate-700 leading-relaxed">Under the Digital Personal Data Protection Act, 2023, you have the right to:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {[
                    { title: 'Access', text: 'Request a summary of your personal data we hold' },
                    { title: 'Correction', text: 'Update or correct inaccurate information' },
                    { title: 'Erasure', text: 'Request deletion of your data (subject to legal retention)' },
                    { title: 'Grievance Redressal', text: 'File complaints with our Data Protection Officer' },
                    { title: 'Withdraw Consent', text: 'Withdraw consent for data processing at any time' },
                    { title: 'Nomination', text: 'Nominate a person to exercise rights on your behalf' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-emerald-50 rounded-lg">
                      <div className="h-2 w-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-900 text-sm m-0">{item.title}</p>
                        <p className="text-sm text-slate-600 m-0">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 7. Data Retention */}
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">7. Data Retention</h2>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li><strong>Active accounts:</strong> Data retained for the duration of the subscription</li>
                  <li><strong>Post-termination:</strong> Data retained for 90 days, then permanently deleted</li>
                  <li><strong>Legal holds:</strong> Data may be retained longer if required by Indian law</li>
                  <li><strong>Backups:</strong> Removed from backup systems within 30 days of deletion</li>
                </ul>
              </section>

              {/* 8. Cookies */}
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">8. Cookies & Tracking</h2>
                <p className="text-slate-700 leading-relaxed">We use essential cookies to:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
                  <li>Maintain your login session (JWT token)</li>
                  <li>Remember your language and theme preferences</li>
                  <li>Analyse platform usage (anonymous analytics)</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-2">
                  We do not use third-party advertising cookies. You can control cookies via your browser settings.
                </p>
              </section>

              {/* 9. Children */}
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">9. Children's Privacy</h2>
                <p className="text-slate-700 leading-relaxed">
                  Ariskool is designed exclusively for school staff (teachers, HR, administrators) aged 18 and above. 
                  We do not collect personal data from students or minors. If you believe a minor's data has been 
                  submitted, contact us immediately for deletion.
                </p>
              </section>

              {/* 10. Contact */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-teal-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 m-0">10. Contact & Grievance Officer</h2>
                </div>
                <div className="bg-slate-50 rounded-lg p-6 mt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Data Protection Officer</h4>
                      <p className="text-sm text-slate-700 m-0">Mr. Arun Sharma</p>
                      <p className="text-sm text-slate-700 m-0">Email: <a href="mailto:privacy@ariskool.in" className="text-teal-600 hover:underline">privacy@ariskool.in</a></p>
                      <p className="text-sm text-slate-700 m-0">Phone: +91 80 4567 8901</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Registered Office</h4>
                      <p className="text-sm text-slate-700 m-0">Ariskool Technologies Pvt. Ltd.</p>
                      <p className="text-sm text-slate-700 m-0">HSR Layout, Sector 2</p>
                      <p className="text-sm text-slate-700 m-0">Bangalore, Karnataka 560102, India</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  We will respond to all privacy-related enquiries within 30 days as required under the DPDP Act, 2023.
                </p>
              </section>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Privacy;
